import { useEffect, useState } from "react";
import { appConfirm } from "@/components/appConfirm";
import { toast } from "sonner";
import { X } from "lucide-react";
import { adminGetBookingSettings, adminPutBookingSettings, adminGetContractTemplate, adminPutContractTemplate, extractApiError } from "@/lib/api";

const APPLIES = { all: "Every contract", service: "Bar / DJ / Nightclub bookings", service_std: "Bar / DJ / Nightclub (not Executive)", executive: "Executive Graduation Experiences only", supply: "Social Supply custom orders", supply_std: "Social Supply (not Executive)", mobile_bar: "The Social Bar bookings", dj: "The Social Set bookings", rentals: "Social Nightclub (inflatable) bookings" };

export const ContractSettingsModal = ({ token, onClose }) => {
  const [retainer, setRetainer] = useState("");
  const [tpl, setTpl] = useState(null);
  const [sections, setSections] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    adminGetBookingSettings(token).then((s) => setRetainer(s.default_retainer ?? "")).catch(() => {});
    adminGetContractTemplate(token).then((t) => { setTpl(t); setSections(t.sections); }).catch(() => {});
  }, [token]);

  const saveRetainer = async () => {
    try { await adminPutBookingSettings(token, { default_retainer: Number(retainer) }); toast.success("Default booking retainer saved"); }
    catch (e) { toast.error(extractApiError(e)); }
  };
  const saveTemplate = async () => {
    if (!(await appConfirm("Publish a new agreement version? Already-signed contracts keep their original wording."))) return;
    try {
      const t = await adminPutContractTemplate(token, { sections, note });
      setTpl({ ...tpl, ...t, versions: [{ version: t.version, created_at: t.created_at, note }, ...(tpl.versions || [])] });
      setNote("");
      toast.success(`Agreement v${t.version} published`);
    } catch (e) { toast.error(extractApiError(e)); }
  };
  const upd = (i, k, v) => setSections(sections.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 md:p-10" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="contract-settings-modal">
      <div className="w-full max-w-4xl p-6 md:p-8 space-y-8" style={{ background: "var(--ss-bg)", color: "var(--ss-cream)" }}>
        <div className="flex justify-between">
          <div className="font-display text-3xl">Booking & Contract Settings</div>
          <button onClick={onClose} aria-label="Close" data-testid="contract-settings-close"><X size={20} /></button>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">Default booking retainer ($)
            <input className="editorial-input" style={{ width: "10rem" }} type="number" step="0.01" value={retainer} onChange={(e) => setRetainer(e.target.value)} data-testid="settings-retainer" />
          </label>
          <button className="btn-outline" onClick={saveRetainer} disabled={!(Number(retainer) > 0)} data-testid="settings-retainer-save">Save</button>
        </div>
        {tpl && (
          <div className="space-y-5">
            <div className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>
              Current agreement: <strong data-testid="settings-template-version">v{tpl.version}</strong>. Use **bold** for bold text and start a line with "- " for a bullet. Missouri wording swaps are kept automatically.
            </div>
            {sections.map((s, i) => (
              <div key={s.key} className="border p-4" style={{ borderColor: "var(--ss-border)" }}>
                <div className="flex flex-wrap gap-3 items-center mb-2">
                  <input className="editorial-input flex-1 font-label text-xs" value={s.title} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`settings-title-${s.key}`} />
                  <select className="editorial-input w-auto text-sm" value={s.applies} onChange={(e) => upd(i, "applies", e.target.value)}>
                    {Object.entries(APPLIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <textarea className="editorial-input text-sm" rows={Math.min(12, s.body.split("\n").length + 1)} value={s.body} onChange={(e) => upd(i, "body", e.target.value)} data-testid={`settings-body-${s.key}`} />
              </div>
            ))}
            <input className="editorial-input text-sm" placeholder="What changed? (saved with the version)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn-primary" onClick={saveTemplate} data-testid="settings-template-save">Publish new agreement version</button>
            <div className="text-xs space-y-1" style={{ color: "var(--ss-muted)" }}>
              {(tpl.versions || []).map((v) => <div key={v.version}>v{v.version} · {new Date(v.created_at).toLocaleString()} · {v.note}</div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
